import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SummaryCard, SummaryCardFormData } from '../types';

const COLLECTION_NAME = 'summaryCards';

export class SummaryService {
  // Get all summary cards
  static async getAllSummaryCards(): Promise<SummaryCard[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SummaryCard[];
    } catch (error) {
      console.error('Error fetching summary cards:', error);
      throw error;
    }
  }

  // Get summary cards by subject
  static async getSummaryCardsBySubject(subject: string): Promise<SummaryCard[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('subject', '==', subject),
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SummaryCard[];
    } catch (error) {
      console.error('Error fetching summary cards by subject:', error);
      throw error;
    }
  }

  // Get summary card by ID
  static async getSummaryCardById(id: string): Promise<SummaryCard | null> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as SummaryCard;
      }
      return null;
    } catch (error) {
      console.error('Error fetching summary card:', error);
      throw error;
    }
  }

  // Create new summary card
  static async createSummaryCard(data: SummaryCardFormData, userId: string): Promise<string> {
    try {
      const now = new Date().toISOString();
      const summaryCard: Omit<SummaryCard, 'id'> = {
        ...data,
        viewCount: 0,
        likeCount: 0,
        createdAt: now,
        updatedAt: now,
        createdBy: userId
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), summaryCard);
      return docRef.id;
    } catch (error) {
      console.error('Error creating summary card:', error);
      throw error;
    }
  }

  // Update summary card
  static async updateSummaryCard(id: string, data: Partial<SummaryCardFormData>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const updateData = {
        ...data,
        updatedAt: new Date().toISOString()
      };
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating summary card:', error);
      throw error;
    }
  }

  // Delete summary card
  static async deleteSummaryCard(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting summary card:', error);
      throw error;
    }
  }

  // Increment view count
  static async incrementViewCount(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentViewCount = docSnap.data().viewCount || 0;
        await updateDoc(docRef, {
          viewCount: currentViewCount + 1
        });
      }
    } catch (error) {
      console.error('Error incrementing view count:', error);
      throw error;
    }
  }

  // Toggle like
  static async toggleLike(id: string, increment: boolean): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentLikeCount = docSnap.data().likeCount || 0;
        const newLikeCount = increment ? currentLikeCount + 1 : Math.max(0, currentLikeCount - 1);
        await updateDoc(docRef, {
          likeCount: newLikeCount
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      throw error;
    }
  }

  // Search summary cards
  static async searchSummaryCards(searchTerm: string): Promise<SummaryCard[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a basic implementation that searches by title
      const q = query(
        collection(db, COLLECTION_NAME),
        where('isPublished', '==', true),
        orderBy('title')
      );
      
      const querySnapshot = await getDocs(q);
      const allCards = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SummaryCard[];

      // Client-side filtering for more comprehensive search
      return allCards.filter(card => 
        card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } catch (error) {
      console.error('Error searching summary cards:', error);
      throw error;
    }
  }

  // Get summary cards with pagination
  static async getSummaryCardsWithPagination(
    pageSize: number = 10,
    lastDoc?: DocumentSnapshot
  ): Promise<{ cards: SummaryCard[], lastDoc: DocumentSnapshot | null }> {
    try {
      let q = query(
        collection(db, COLLECTION_NAME),
        where('isPublished', '==', true),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );

      if (lastDoc) {
        q = query(
          collection(db, COLLECTION_NAME),
          where('isPublished', '==', true),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        );
      }

      const querySnapshot = await getDocs(q);
      const cards = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SummaryCard[];

      const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;

      return { cards, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching paginated summary cards:', error);
      throw error;
    }
  }

  // Admin: Get all summary cards (including unpublished)
  static async getAllSummaryCardsForAdmin(): Promise<SummaryCard[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SummaryCard[];
    } catch (error) {
      console.error('Error fetching all summary cards for admin:', error);
      throw error;
    }
  }

  // Toggle publish status
  static async togglePublishStatus(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const currentStatus = docSnap.data().isPublished || false;
        await updateDoc(docRef, {
          isPublished: !currentStatus,
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error toggling publish status:', error);
      throw error;
    }
  }
}

export default SummaryService;